import { useCallback, useEffect, useRef, useState } from 'react';
import * as clubApi from '@/services/clubs';
import * as teamApi from '@/services/teams';
import { track, AnalyticsEvent } from '@/lib/analytics';

export type TeamCategory = 'Senior' | 'U21' | 'U20' | 'U18' | 'U16' | 'U14' | 'Academy';
export type Gender = 'Men' | 'Women' | 'Mixed';
export type InvitationStatus = 'pending' | 'accepted' | 'rejected';
export type StaffRole =
  | 'Head Coach'
  | 'Assistant Coach'
  | 'Skills Coach'
  | 'Strength & Conditioning Coach'
  | 'Physical Trainer'
  | 'Scout'
  | 'Team Manager'
  | 'Medical Staff'
  | 'Club Administrator';

export const STAFF_ROLES: StaffRole[] = [
  'Head Coach',
  'Assistant Coach',
  'Skills Coach',
  'Strength & Conditioning Coach',
  'Physical Trainer',
  'Scout',
  'Team Manager',
  'Medical Staff',
  'Club Administrator',
];

export const TEAM_CATEGORIES: TeamCategory[] = ['Senior', 'U21', 'U20', 'U18', 'U16', 'U14', 'Academy'];
export const GENDERS: Gender[] = ['Men', 'Women', 'Mixed'];

export type InvitationType = 'player' | 'staff';

export type ClubVerificationStatus = 'not_submitted' | 'pending' | 'approved' | 'rejected';

export interface ClubVerificationRequest {
  id: string;
  clubId: string;
  officialName: string;
  officialEmail: string;
  officialWebsite: string;
  proofReference: string;
  notes?: string;
  status: ClubVerificationStatus;
  createdAt: string;
}

export interface Club {
  id: string;
  ownerId: string;
  name: string;
  initials: string;
  bio?: string;
  location?: string;
  avatarUrl?: string;
  verified: boolean;
  foundedYear?: number;
  website?: string;
  instagram?: string;
  facebook?: string;
  createdAt: string;
}

export interface Team {
  id: string;
  clubId: string;
  name: string;
  category: TeamCategory;
  gender: Gender;
  division: string;
  season?: string;
  createdAt: string;
}

export interface RosterEntry {
  id: string;
  teamId: string;
  playerId: string;
  jerseyNumber: number;
  position: string;
  height?: string;
  age?: number;
  createdAt: string;
}

export interface StaffEntry {
  id: string;
  clubId: string;
  teamId?: string;
  userId: string;
  role: StaffRole;
  createdAt: string;
}

export interface Invitation {
  id: string;
  clubId: string;
  teamId?: string;
  targetUserId: string;
  type: InvitationType;
  staffRole?: StaffRole;
  status: InvitationStatus;
  createdAt: string;
}

export interface Match {
  id: string;
  teamId: string;
  opponent: string;
  date: string;
  location: string;
  teamScore?: number;
  opponentScore?: number;
  createdAt: string;
}

export interface PlayerMatchStats {
  id: string;
  playerId: string;
  matchId: string;
  points: number;
  rebounds: number;
  assists: number;
  steals: number;
  blocks: number;
  createdAt: string;
}

export interface PlayerAverageStats {
  points: number;
  rebounds: number;
  assists: number;
  steals: number;
  blocks: number;
  gamesPlayed: number;
}

const mergeById = <T extends { id: string }>(prev: T[], next: T[]): T[] => {
  const map = new Map(prev.map((item) => [item.id, item]));
  next.forEach((item) => map.set(item.id, item));
  return Array.from(map.values());
};

/**
 * Backend-driven club store (clubs, teams, rosters, staff, invitations,
 * matches, player statistics and verification requests).
 */
export function useClubStore(currentUserId: string) {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [staff, setStaff] = useState<StaffEntry[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [playerStats, setPlayerStats] = useState<PlayerMatchStats[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [verificationRequests, setVerificationRequests] = useState<ClubVerificationRequest[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const loadedTeams = useRef<Set<string>>(new Set());
  const loadedPlayers = useRef<Set<string>>(new Set());

  /** Load teams / rosters / staff / invitations attached to a set of clubs. */
  const hydrateClubs = useCallback(async (clubList: Club[]) => {
    if (clubList.length === 0) return;
    const clubIds = clubList.map((c) => c.id);
    const [teamRows, staffRows, invitationRows] = await Promise.all([
      teamApi.fetchTeamsForClubs(clubIds),
      clubApi.fetchStaffForClubs(clubIds),
      clubApi.fetchInvitationsForClubs(clubIds).catch(() => [] as Invitation[]),
    ]);
    setTeams((prev) => mergeById(prev, teamRows));
    setStaff((prev) => mergeById(prev, staffRows));
    setInvitations((prev) => mergeById(prev, invitationRows));
    const rosterRows = await teamApi.fetchRosterForTeams(teamRows.map((t) => t.id));
    setRoster((prev) => mergeById(prev, rosterRows));
  }, []);

  const loadInitial = useCallback(async () => {
    if (!currentUserId) return;
    setLoading(true);
    setError(null);
    try {
      const [{ items, hasMore: more }, userInvitations, myRoster] = await Promise.all([
        clubApi.fetchClubsPage(0),
        clubApi.fetchInvitationsForUser(currentUserId),
        teamApi.fetchRosterForPlayer(currentUserId),
      ]);

      setPage(0);
      setHasMore(more);
      setInvitations((prev) => mergeById(prev, userInvitations));
      setRoster((prev) => mergeById(prev, myRoster));

      // Clubs referenced by the user's own memberships / invitations
      const extraTeamIds = myRoster.map((r) => r.teamId);
      const extraTeams = extraTeamIds.length
        ? (await Promise.all(extraTeamIds.map((id) => teamApi.fetchTeamById(id)))).filter(
            (t): t is Team => !!t,
          )
        : [];
      setTeams((prev) => mergeById(prev, extraTeams));

      const relatedClubIds = Array.from(
        new Set([
          ...extraTeams.map((t) => t.clubId),
          ...userInvitations.map((i) => i.clubId),
        ]),
      ).filter((id) => !items.some((c) => c.id === id));
      const relatedClubs = await clubApi.fetchClubsByIds(relatedClubIds);

      const allClubs = mergeById(items, relatedClubs);
      setClubs((prev) => mergeById(prev, allClubs));
      await hydrateClubs(allClubs);

      const verifications = await clubApi.fetchVerificationRequests().catch(() => []);
      setVerificationRequests(verifications);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load clubs');
    } finally {
      setLoading(false);
    }
  }, [currentUserId, hydrateClubs]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    setLoading(true);
    try {
      const next = page + 1;
      const { items, hasMore: more } = await clubApi.fetchClubsPage(next);
      setPage(next);
      setHasMore(more);
      setClubs((prev) => mergeById(prev, items));
      await hydrateClubs(items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load clubs');
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, page, hydrateClubs]);

  const refresh = useCallback(async () => {
    loadedTeams.current.clear();
    loadedPlayers.current.clear();
    await loadInitial();
  }, [loadInitial]);

  /** Fetch a single club (plus its teams/roster/staff) when not already loaded. */
  const ensureClub = useCallback(
    async (clubId: string) => {
      if (!clubId) return;
      const club = await clubApi.fetchClubById(clubId);
      if (!club) return;
      setClubs((prev) => mergeById(prev, [club]));
      await hydrateClubs([club]);
    },
    [hydrateClubs],
  );

  /** Fetch matches + statistics for a team (lazy, once per team). */
  const ensureTeamData = useCallback(async (teamId: string) => {
    if (!teamId || loadedTeams.current.has(teamId)) return;
    loadedTeams.current.add(teamId);
    try {
      const teamMatches = await teamApi.fetchMatchesForTeam(teamId);
      setMatches((prev) => mergeById(prev, teamMatches));
      const stats = await teamApi.fetchStatsForMatches(teamMatches.map((m) => m.id));
      setPlayerStats((prev) => mergeById(prev, stats));
    } catch {
      loadedTeams.current.delete(teamId);
    }
  }, []);

  /** Fetch every statistic line of a player (lazy, once per player). */
  const ensurePlayerStats = useCallback(async (playerId: string) => {
    if (!playerId || loadedPlayers.current.has(playerId)) return;
    loadedPlayers.current.add(playerId);
    try {
      const stats = await teamApi.fetchStatsForPlayer(playerId);
      setPlayerStats((prev) => mergeById(prev, stats));
    } catch {
      loadedPlayers.current.delete(playerId);
    }
  }, []);

  // ------------------------------------------------------------- selectors

  const getClub = useCallback((id: string) => clubs.find((c) => c.id === id), [clubs]);

  const getClubByOwner = useCallback(
    (ownerId: string) => clubs.find((c) => c.ownerId === ownerId),
    [clubs],
  );

  const getTeamsForClub = useCallback(
    (clubId: string) => teams.filter((t) => t.clubId === clubId),
    [teams],
  );

  const getTeam = useCallback((id: string) => teams.find((t) => t.id === id), [teams]);

  const getRosterForTeam = useCallback(
    (teamId: string) => roster.filter((r) => r.teamId === teamId),
    [roster],
  );

  const getPendingInvitationsForTeam = useCallback(
    (teamId: string) =>
      invitations.filter((i) => i.teamId === teamId && i.type === 'player' && i.status === 'pending'),
    [invitations],
  );

  const getInvitationsForUser = useCallback(
    (userId: string) => invitations.filter((i) => i.targetUserId === userId),
    [invitations],
  );

  const isClubOwner = useCallback(
    (clubId: string) => clubs.find((c) => c.id === clubId)?.ownerId === currentUserId,
    [clubs, currentUserId],
  );

  const getPlayerMembership = useCallback(
    (playerId: string) => {
      const entry = roster.find((r) => r.playerId === playerId);
      if (!entry) return null;
      const team = teams.find((t) => t.id === entry.teamId);
      if (!team) return null;
      const club = clubs.find((c) => c.id === team.clubId);
      if (!club) return null;
      return { club, team, roster: entry };
    },
    [roster, teams, clubs],
  );

  const getStaffForClub = useCallback(
    (clubId: string) => staff.filter((s) => s.clubId === clubId),
    [staff],
  );

  const getStaffForTeam = useCallback(
    (teamId: string) => staff.filter((s) => s.teamId === teamId),
    [staff],
  );

  const getMatchesForTeam = useCallback(
    (teamId: string) =>
      matches
        .filter((m) => m.teamId === teamId)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [matches],
  );

  const getStatsForMatch = useCallback(
    (matchId: string) => playerStats.filter((s) => s.matchId === matchId),
    [playerStats],
  );

  const getStatsForPlayer = useCallback(
    (playerId: string) => playerStats.filter((s) => s.playerId === playerId),
    [playerStats],
  );

  const getPlayerAverages = useCallback(
    (playerId: string): PlayerAverageStats | null => {
      const stats = playerStats.filter((s) => s.playerId === playerId);
      const n = stats.length;
      if (n === 0) return null;
      const avg = (sum: number) => Math.round((sum / n) * 10) / 10;
      return {
        points: avg(stats.reduce((a, s) => a + s.points, 0)),
        rebounds: avg(stats.reduce((a, s) => a + s.rebounds, 0)),
        assists: avg(stats.reduce((a, s) => a + s.assists, 0)),
        steals: avg(stats.reduce((a, s) => a + s.steals, 0)),
        blocks: avg(stats.reduce((a, s) => a + s.blocks, 0)),
        gamesPlayed: n,
      };
    },
    [playerStats],
  );

  const getVerificationForClub = useCallback(
    (clubId: string) => verificationRequests.find((v) => v.clubId === clubId),
    [verificationRequests],
  );

  const getAllVerificationRequests = useCallback(() => verificationRequests, [verificationRequests]);

  // ------------------------------------------------------------- mutations

  const createClub = useCallback(
    async (data: { name: string; bio?: string; location?: string; avatarUrl?: string }) => {
      const club = await clubApi.insertClub(currentUserId, data);
      setClubs((prev) => mergeById(prev, [club]));
      void track(AnalyticsEvent.CLUB_CREATED, { club_id: club.id });
      return club.id;
    },
    [currentUserId],
  );

  const updateClub = useCallback(
    async (
      clubId: string,
      data: { name?: string; bio?: string; location?: string; avatarUrl?: string | null },
    ) => {
      setClubs((prev) =>
        prev.map((c) => {
          if (c.id !== clubId) return c;
          const name = data.name?.trim() || c.name;
          return {
            ...c,
            name,
            initials: clubApi.initialsFor(name),
            bio: data.bio !== undefined ? data.bio.trim() || undefined : c.bio,
            location: data.location !== undefined ? data.location.trim() || undefined : c.location,
            avatarUrl:
              data.avatarUrl === null ? undefined : data.avatarUrl !== undefined ? data.avatarUrl : c.avatarUrl,
          };
        }),
      );
      await clubApi.patchClub(clubId, data);
    },
    [],
  );

  const createTeam = useCallback(
    async (data: {
      clubId: string;
      name: string;
      category: TeamCategory;
      gender: Gender;
      division: string;
      season?: string;
    }) => {
      const team = await teamApi.insertTeam(data);
      setTeams((prev) => mergeById(prev, [team]));
      return team.id;
    },
    [],
  );

  const removeTeam = useCallback(async (teamId: string) => {
    setTeams((prev) => prev.filter((t) => t.id !== teamId));
    setRoster((prev) => prev.filter((r) => r.teamId !== teamId));
    setStaff((prev) => prev.map((s) => (s.teamId === teamId ? { ...s, teamId: undefined } : s)));
    await teamApi.removeTeamRow(teamId);
  }, []);

  const addRosterEntry = useCallback(
    async (data: {
      teamId: string;
      playerId: string;
      jerseyNumber: number;
      position: string;
      height?: string;
      age?: number;
    }) => {
      const entry = await teamApi.insertRosterEntry(data);
      if (entry) setRoster((prev) => mergeById(prev, [entry]));
    },
    [],
  );

  const removeRosterEntry = useCallback(async (entryId: string) => {
    setRoster((prev) => prev.filter((r) => r.id !== entryId));
    await teamApi.removeRosterRow(entryId);
  }, []);

  const removePlayerFromTeam = useCallback(async (teamId: string, playerId: string) => {
    setRoster((prev) => prev.filter((r) => !(r.teamId === teamId && r.playerId === playerId)));
    await teamApi.removePlayerFromTeamRow(teamId, playerId);
  }, []);

  const movePlayerToTeam = useCallback(
    async (playerId: string, fromTeamId: string, toTeamId: string) => {
      if (fromTeamId === toTeamId) return;
      const entry = roster.find((r) => r.teamId === fromTeamId && r.playerId === playerId);
      if (!entry) return;
      const alreadyThere = roster.some((r) => r.teamId === toTeamId && r.playerId === playerId);
      if (alreadyThere) {
        setRoster((prev) => prev.filter((r) => r.id !== entry.id));
        await teamApi.removeRosterRow(entry.id);
        return;
      }
      setRoster((prev) => prev.map((r) => (r.id === entry.id ? { ...r, teamId: toTeamId } : r)));
      await teamApi.moveRosterEntry(entry.id, toTeamId);
    },
    [roster],
  );

  const addStaff = useCallback(
    async (data: { clubId: string; userId: string; role: StaffRole; teamId?: string }) => {
      const entry = await clubApi.insertStaff(data);
      if (entry) setStaff((prev) => mergeById(prev, [entry]));
    },
    [],
  );

  const removeStaff = useCallback(async (staffId: string) => {
    setStaff((prev) => prev.filter((s) => s.id !== staffId));
    await clubApi.removeStaffRow(staffId);
  }, []);

  const updateStaffRole = useCallback(async (staffId: string, role: StaffRole) => {
    setStaff((prev) => prev.map((s) => (s.id === staffId ? { ...s, role } : s)));
    await clubApi.patchStaff(staffId, { role });
  }, []);

  const updateStaffTeam = useCallback(async (staffId: string, teamId: string | undefined) => {
    setStaff((prev) => prev.map((s) => (s.id === staffId ? { ...s, teamId } : s)));
    await clubApi.patchStaff(staffId, { teamId: teamId ?? null });
  }, []);

  const invitePlayer = useCallback(
    async (clubId: string, teamId: string, playerId: string) => {
      const onRoster = roster.some((r) => r.teamId === teamId && r.playerId === playerId);
      const alreadyInvited = invitations.some(
        (i) =>
          i.teamId === teamId &&
          i.targetUserId === playerId &&
          i.type === 'player' &&
          i.status === 'pending',
      );
      if (onRoster || alreadyInvited) return;
      const inv = await clubApi.insertInvitation({
        clubId,
        teamId,
        targetUserId: playerId,
        invitedBy: currentUserId,
        type: 'player',
      });
      if (inv) setInvitations((prev) => mergeById(prev, [inv]));
    },
    [roster, invitations, currentUserId],
  );

  const inviteStaff = useCallback(
    async (clubId: string, userId: string, role: StaffRole, teamId?: string) => {
      const alreadyStaff = staff.some(
        (s) => s.clubId === clubId && s.userId === userId && s.teamId === teamId,
      );
      const alreadyInvited = invitations.some(
        (i) =>
          i.clubId === clubId &&
          i.targetUserId === userId &&
          i.type === 'staff' &&
          i.status === 'pending',
      );
      if (alreadyStaff || alreadyInvited) return;
      const inv = await clubApi.insertInvitation({
        clubId,
        teamId,
        targetUserId: userId,
        invitedBy: currentUserId,
        type: 'staff',
        staffRole: role,
      });
      if (inv) setInvitations((prev) => mergeById(prev, [inv]));
    },
    [staff, invitations, currentUserId],
  );

  const respondToInvitation = useCallback(
    async (invitationId: string, accept: boolean) => {
      const inv = invitations.find((i) => i.id === invitationId);
      if (!inv || inv.status !== 'pending') return;

      setInvitations((prev) =>
        prev.map((i) => (i.id === invitationId ? { ...i, status: accept ? 'accepted' : 'rejected' } : i)),
      );
      await clubApi.setInvitationStatus(invitationId, accept ? 'accepted' : 'rejected');

      if (!accept) return;

      if (inv.type === 'player' && inv.teamId) {
        const entry = await teamApi.insertRosterEntry({
          teamId: inv.teamId,
          playerId: inv.targetUserId,
          jerseyNumber: 0,
          position: 'TBD',
        });
        if (entry) setRoster((prev) => mergeById(prev, [entry]));
      } else if (inv.type === 'staff') {
        const entry = await clubApi.insertStaff({
          clubId: inv.clubId,
          userId: inv.targetUserId,
          role: inv.staffRole ?? 'Team Manager',
          teamId: inv.teamId,
        });
        if (entry) setStaff((prev) => mergeById(prev, [entry]));
      }
    },
    [invitations],
  );

  const createMatch = useCallback(
    async (data: {
      teamId: string;
      opponent: string;
      date: string;
      location: string;
      teamScore?: number;
      opponentScore?: number;
    }) => {
      const match = await teamApi.insertMatch(data);
      setMatches((prev) => mergeById(prev, [match]));
      return match.id;
    },
    [],
  );

  const updateMatchScore = useCallback(
    async (matchId: string, teamScore: number, opponentScore: number) => {
      setMatches((prev) =>
        prev.map((m) => (m.id === matchId ? { ...m, teamScore, opponentScore } : m)),
      );
      await teamApi.patchMatchScore(matchId, teamScore, opponentScore);
    },
    [],
  );

  const upsertPlayerStats = useCallback(
    async (data: {
      playerId: string;
      matchId: string;
      points: number;
      rebounds: number;
      assists: number;
      steals: number;
      blocks: number;
    }) => {
      const row = await teamApi.upsertStats(data);
      setPlayerStats((prev) => mergeById(prev, [row]));
    },
    [],
  );

  const submitVerificationRequest = useCallback(
    async (data: {
      clubId: string;
      officialName: string;
      officialEmail: string;
      officialWebsite: string;
      proofReference: string;
      notes?: string;
    }) => {
      const existing = verificationRequests.find(
        (v) => v.clubId === data.clubId && v.status === 'pending',
      );
      if (existing) return;
      const req = await clubApi.insertVerificationRequest(data);
      if (req) setVerificationRequests((prev) => mergeById(prev, [req]));
    },
    [verificationRequests],
  );

  const reviewVerificationRequest = useCallback(
    async (requestId: string, approve: boolean) => {
      const req = verificationRequests.find((v) => v.id === requestId);
      setVerificationRequests((prev) =>
        prev.map((v) =>
          v.id === requestId ? { ...v, status: approve ? ('approved' as const) : ('rejected' as const) } : v,
        ),
      );
      await clubApi.reviewVerification(requestId, approve, req?.clubId);
      if (approve && req) {
        setClubs((prev) => prev.map((c) => (c.id === req.clubId ? { ...c, verified: true } : c)));
      }
    },
    [verificationRequests],
  );

  /** Remove every trace of a user from club data. */
  const purgeUserData = useCallback(async (userId: string) => {
    const ownedClubIds = new Set(clubs.filter((c) => c.ownerId === userId).map((c) => c.id));
    const ownedTeamIds = new Set(teams.filter((t) => ownedClubIds.has(t.clubId)).map((t) => t.id));
    setClubs((prev) => prev.filter((c) => !ownedClubIds.has(c.id)));
    setTeams((prev) => prev.filter((t) => !ownedClubIds.has(t.clubId)));
    setRoster((prev) => prev.filter((r) => !ownedTeamIds.has(r.teamId) && r.playerId !== userId));
    setStaff((prev) => prev.filter((s) => !ownedClubIds.has(s.clubId) && s.userId !== userId));
    await clubApi.purgeUserClubData(userId);
  }, [clubs, teams]);

  return {
    clubs,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    ensureClub,
    ensureTeamData,
    ensurePlayerStats,
    getClub,
    getClubByOwner,
    getTeamsForClub,
    getTeam,
    getRosterForTeam,
    createTeam,
    removeTeam,
    addRosterEntry,
    removeRosterEntry,
    removePlayerFromTeam,
    movePlayerToTeam,
    getPendingInvitationsForTeam,
    addStaff,
    createClub,
    updateClub,
    invitePlayer,
    inviteStaff,
    getInvitationsForPlayer: getInvitationsForUser,
    getInvitationsForUser,
    respondToInvitation,
    isClubOwner,
    getPlayerMembership,
    getStaffForClub,
    getStaffForTeam,
    removeStaff,
    updateStaffRole,
    updateStaffTeam,
    purgeUserData,
    getMatchesForTeam,
    createMatch,
    updateMatchScore,
    getStatsForMatch,
    getStatsForPlayer,
    getPlayerAverages,
    upsertPlayerStats,
    verificationRequests,
    getVerificationForClub,
    getAllVerificationRequests,
    submitVerificationRequest,
    reviewVerificationRequest,
  };
}
